import { Link } from "@tanstack/react-router";
import { Topbar } from "../Topbar";
import { Footer } from "../Footer";
import { Hero } from "../Hero";
import { Section } from "../Section";
import { CTABlock } from "../CTABlock";
import { RevealOnScroll } from "../RevealOnScroll";
import { formatDate, type BlogLocale, type BlogPost } from "@/lib/blog";
import { pathMap, SITE_URL } from "@/lib/locale";

type Props = { locale: BlogLocale; posts: BlogPost[] };

export function BlogOverview({ locale, posts }: Props) {
  const isEn = locale === "en";

  const tr = isEn
    ? {
        eyebrow: "Blog",
        headlinePre: "Substance on ",
        headlineEm: "performance, SEO and stack discipline.",
        lead: "Here we collect articles on topics that are economically relevant in our mandate practice — no industry news, no SEO generics, but editorial-substantial explanations with practical reference.",
        meta: ["Editorial", "Independent", "From mandate practice"],
        kicker: "Articles",
        sectionHead: "Currently published",
        empty: "First articles coming soon.",
        readPost: "Read post →",
        minRead: "min read",
        ctaHead: "Want a mandate-grade conversation, not generic advice?",
        ctaBody:
          "If you're looking for substance on a specific performance, SEO or stack question — let's talk. We answer with the same clarity we write with.",
        ctaPrimary: "Start a conversation",
        ctaSecondary: "All services",
        breadHome: "Start",
      }
    : {
        eyebrow: "Blog",
        headlinePre: "Substanz zu ",
        headlineEm: "Performance, SEO und Stack-Disziplin.",
        lead: "Hier sammeln wir Beiträge zu Themen, die in unserer Mandate-Praxis wirtschaftlich relevant sind — keine Branchen-News, keine SEO-Generika, sondern editorial-substantielle Erklärungen mit Praxis-Bezug.",
        meta: ["Editorial", "Unabhängig", "Aus der Mandate-Praxis"],
        kicker: "Beiträge",
        sectionHead: "Aktuell veröffentlicht",
        empty: "Erste Beiträge folgen in Kürze.",
        readPost: "Beitrag lesen →",
        minRead: "Min Lesezeit",
        ctaHead: "Substanz statt Generika — sprechen wir?",
        ctaBody:
          "Wenn Sie zu einer konkreten Performance-, SEO- oder Stack-Frage Substanz suchen — sprechen wir. Wir antworten mit der gleichen Klarheit, mit der wir schreiben.",
        ctaPrimary: "Erst-Gespräch anfragen",
        ctaSecondary: "Alle Leistungen",
        breadHome: "Start",
      };

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    url: `${SITE_URL}${isEn ? pathMap.blog.en : pathMap.blog.de}`,
    name: "Calvarius Blog",
    description: tr.lead,
    publisher: {
      "@type": "Organization",
      name: "Calvarius GmbH",
      url: SITE_URL,
    },
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.fm.title,
      url: `${SITE_URL}${isEn ? `/en/blog/${p.fm.slug}` : `/blog/${p.fm.slug}`}`,
      datePublished: p.fm.datePublished,
      dateModified: p.fm.dateModified,
      author: { "@type": "Organization", name: "Calvarius GmbH" },
    })),
  };

  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow={tr.eyebrow}
          headline={
            <>
              {tr.headlinePre}
              <em>{tr.headlineEm}</em>
            </>
          }
          lead={tr.lead}
          metaStrip={tr.meta}
          breadcrumbs={[
            { label: tr.breadHome, to: isEn ? "/en" : "/" },
            { label: tr.eyebrow },
          ]}
        />

        <Section number="02" kicker={tr.kicker} headline={<em>{tr.sectionHead}.</em>}>
          {posts.length === 0 ? (
            <p className="text-ink-3">{tr.empty}</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((p, i) => {
                const href = isEn ? `/en/blog/${p.fm.slug}` : `/blog/${p.fm.slug}`;
                const preview = p.bodyHtml
                  .replace(/<[^>]+>/g, " ")
                  .replace(/\s+/g, " ")
                  .trim()
                  .slice(0, 180);
                return (
                  <RevealOnScroll key={p.fm.slug} delay={i * 60}>
                    <Link
                      to={href}
                      className="group flex h-full flex-col rounded-lg border border-rule bg-white p-7 transition-colors hover:border-cyan-deep/50"
                    >
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-4">
                        {p.fm.category}
                      </div>
                      <h2 className="mt-4 font-display text-2xl leading-tight text-ink">
                        {p.fm.title}
                      </h2>
                      <p className="mt-4 leading-relaxed text-ink-3 flex-1 text-[15px]">
                        {preview}…
                      </p>
                      <div className="mt-5 flex items-center justify-between font-mono text-[11px] uppercase tracking-wider text-ink-4">
                        <span>{formatDate(p.fm.datePublished, locale)}</span>
                        <span>{p.fm.readingTimeMinutes} {tr.minRead}</span>
                      </div>
                      <div className="mt-4 font-mono text-xs uppercase tracking-wider text-cyan-deep group-hover:underline">
                        {tr.readPost}
                      </div>
                    </Link>
                  </RevealOnScroll>
                );
              })}
            </div>
          )}
        </Section>

        <Section number="03" variant="deep">
          <CTABlock
            variant="deep"
            headline={<em>{tr.ctaHead}</em>}
            body={tr.ctaBody}
            primary={{ label: tr.ctaPrimary, to: isEn ? pathMap.contact.en : pathMap.contact.de }}
            secondary={{ label: tr.ctaSecondary, to: isEn ? pathMap.services.en : pathMap.services.de }}
          />
        </Section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogSchema) }}
      />
    </>
  );
}
